/**
 * Price-alert evaluation engine.
 *
 * Runs a polling loop every 5 seconds and checks every active (non-triggered)
 * price alert against the latest LTP data available from two sources:
 * 1. The `watchlist.scrips` Redux state (symbols in the market-watch panel).
 * 2. The `chart.oneMinuteData` Redux state (symbols open in chart panels but
 *    not necessarily in the watchlist).
 *
 * ### Supported conditions
 * - `above`          — triggers when `ltp >= targetPrice`.
 * - `below`          — triggers when `ltp <= targetPrice`.
 * - `crosses_above`  — triggers on the first tick where LTP crosses upward
 *                      through `targetPrice`.
 * - `crosses_below`  — triggers on the first tick where LTP crosses downward
 *                      through `targetPrice`.
 *
 * On trigger: dispatches `triggerAlert` to the Redux store (creates a
 * notification), and plays a short Web Audio API beep if sound is enabled.
 */
/**
 * Alert Evaluation Engine
 * Runs every 5 seconds and checks all active (non-triggered) alerts
 * against the latest LTP from the watchlist store.
 */
import { store } from "../store";
import { triggerAlert, PriceAlert } from "../store/alert-slice";
import { ScripState } from "../store/watchlist-slice";
import toast from "react-hot-toast";

class AlertEvaluationEngine {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly EVAL_INTERVAL_MS = 5000; // 5 seconds
  /** Track previous LTP per symbol for "crosses" conditions */
  private prevLtpMap: Map<string, number> = new Map();

  start() {
    if (this.intervalId) return; // Already running
    this.intervalId = setInterval(() => this.evaluate(), this.EVAL_INTERVAL_MS);
    console.log("[AlertEngine] Started — evaluating every 5s");
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log("[AlertEngine] Stopped");
    }
  }

  private evaluate() {
    const state = store.getState();
    const alerts: PriceAlert[] = state.alerts?.alerts || [];
    const scrips: ScripState[] = state.watchlist?.scrips || [];

    if (alerts.length === 0) return;

    // Build a quick lookup: symbol → LTP
    const ltpMap = new Map<string, number>();
    for (const scrip of scrips) {
      if (scrip.ltp != null) {
        ltpMap.set(scrip.symbol, scrip.ltp);
      }
    }

    // Also check chart one-minute data for symbols not in watchlist
    const oneMinuteData = state.chart?.oneMinuteData || {};
    for (const [symbol, bars] of Object.entries(oneMinuteData)) {
      if (!ltpMap.has(symbol) && Array.isArray(bars) && bars.length > 0) {
        const lastBar = bars[bars.length - 1] as any;
        if (lastBar?.close != null) {
          ltpMap.set(symbol, lastBar.close);
        }
      }
    }

    for (const alert of alerts) {
      if (alert.triggered) continue;

      const currentLtp = ltpMap.get(alert.symbol);
      if (currentLtp == null) continue;

      const prevLtp = this.prevLtpMap.get(alert.symbol);
      let shouldTrigger = false;

      switch (alert.condition) {
        case "above":
          shouldTrigger = currentLtp >= alert.targetPrice;
          break;
        case "below":
          shouldTrigger = currentLtp <= alert.targetPrice;
          break;
        case "crosses_above":
          // Previous LTP was below target and current is above
          if (prevLtp != null) {
            shouldTrigger = prevLtp < alert.targetPrice && currentLtp >= alert.targetPrice;
          } else {
            // First evaluation — check if currently above and was created below
            shouldTrigger = alert.createdAtPrice < alert.targetPrice && currentLtp >= alert.targetPrice;
          }
          break;
        case "crosses_below":
          // Previous LTP was above target and current is below
          if (prevLtp != null) {
            shouldTrigger = prevLtp > alert.targetPrice && currentLtp <= alert.targetPrice;
          } else {
            shouldTrigger = alert.createdAtPrice > alert.targetPrice && currentLtp <= alert.targetPrice;
          }
          break;
      }

      if (shouldTrigger) {
        store.dispatch(triggerAlert({ alertId: alert.id, currentPrice: currentLtp }));

        // Show in-app toast notification
        const condLabel = alert.condition.replace(/_/g, " ");
        const toastMsg = `${alert.symbol} ${condLabel} ₹${alert.targetPrice.toLocaleString("en-IN")}`;
        toast(`🔔 ${toastMsg}`, { duration: 6000 });

        // Fire OS-level notification (best-effort — Tauri notification plugin)
        this.sendOSNotification(
          `Price Alert: ${alert.symbol}`,
          toastMsg
        );

        // Play sound if enabled
        if (state.alerts?.alertSoundEnabled) {
          this.playAlertSound();
        }
      }
    }

    // Update previous LTP map for cross detection
    ltpMap.forEach((ltp, symbol) => {
      this.prevLtpMap.set(symbol, ltp);
    });
  }

  private playAlertSound() {
    try {
      // Use the Web Audio API to play a short beep
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      oscillator.frequency.value = 880; // A5 note
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.5);

      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.5);
    } catch {
      // Sound not available — no-op
    }
  }

  /**
   * Send an OS-level notification via Tauri's notification plugin.
   * Falls back to the browser Notification API if the Tauri plugin is
   * unavailable (e.g. during `npm start` / dev-server without Tauri).
   */
  private async sendOSNotification(title: string, body: string) {
    try {
      // Try Tauri notification plugin first
      const { sendNotification, isPermissionGranted, requestPermission } =
        await import("@tauri-apps/plugin-notification");
      let permitted = await isPermissionGranted();
      if (!permitted) {
        const result = await requestPermission();
        permitted = result === "granted";
      }
      if (permitted) {
        sendNotification({ title, body });
        return;
      }
    } catch {
      // Tauri plugin not available — try browser Notification API
    }

    try {
      if ("Notification" in window) {
        if (Notification.permission === "granted") {
          new Notification(title, { body });
        } else if (Notification.permission !== "denied") {
          const perm = await Notification.requestPermission();
          if (perm === "granted") {
            new Notification(title, { body });
          }
        }
      }
    } catch {
      // No notification mechanism available — silently ignore
    }
  }
}

export const alertEngine = new AlertEvaluationEngine();
